import { Activity, Bone, HeartPulse, Stethoscope, Waves, Dumbbell } from "lucide-react";

const SERVICES = [
  {
    id: "ortho",
    title: "Orthopedic Rehab",
    icon: Bone,
    blurb:
      "Targeted recovery for joint pain, fractures, frozen shoulder, slipped disc and chronic back pain.",
    tone: "image",
    image:
      "https://images.pexels.com/photos/4506075/pexels-photo-4506075.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
  {
    id: "sports",
    title: "Sports Physiotherapy",
    icon: Dumbbell,
    blurb:
      "Return-to-play protocols for athletes — from strain management to performance conditioning.",
    tone: "image",
    image:
      "https://images.pexels.com/photos/5275246/pexels-photo-5275246.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
  {
    id: "post-surgical",
    title: "Post-Surgical Recovery",
    icon: Stethoscope,
    blurb:
      "Structured rehabilitation following ACL, knee replacement, spine and shoulder surgeries.",
    tone: "image",
    image:
      "https://images.pexels.com/photos/6111622/pexels-photo-6111622.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
  {
    id: "pain-relief",
    title: "Chronic Pain Relief",
    icon: HeartPulse,
    blurb:
      "Manual therapy, electrotherapy & dry needling for long-standing neck, back and joint pain.",
    tone: "image",
    image:
      "https://images.pexels.com/photos/5793653/pexels-photo-5793653.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
  {
    id: "mobility",
    title: "Mobility & Movement",
    icon: Activity,
    blurb:
      "Posture correction, gait training and flexibility programs designed for every body.",
    tone: "image",
    image:
      "https://images.pexels.com/photos/5201859/pexels-photo-5201859.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
  {
    id: "neuro",
    title: "Neuro Rehabilitation",
    icon: Waves,
    blurb:
      "Recovery support for stroke, Parkinson's and post-neurological conditions, restoring function step by step.",
    tone: "image",
    image:
      "https://images.pexels.com/photos/6094336/pexels-photo-6094336.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-16 md:py-32 lg:py-40 bg-sand-50"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-20">
          <div className="col-span-12 md:col-span-5">
            <span className="inline-flex items-center gap-3 text-xs tracking-[0.28em] uppercase text-forest-700">
              <span className="w-10 h-px bg-terracotta-500" />
              What we treat
            </span>
            <h2 className="mt-5 font-serif text-5xl md:text-6xl leading-[1.02] tracking-tight text-forest-900">
              Care for every <em className="text-terracotta-500">ripple</em> of recovery.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 self-end">
            <p className="text-lg text-forest-700/85 leading-relaxed">
              Every plan is hand-crafted around your lifestyle, history and
              goals. No two bodies heal the same — and neither do our
              programmes.
            </p>
          </div>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-12 gap-4 md:gap-6 auto-rows-[200px] sm:auto-rows-[220px] md:auto-rows-[260px]">
          {SERVICES.map((s, i) => (
            <ServiceTile key={s.id} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceTile({ service, index }) {
  const Icon = service.icon;
  // Asymmetric span pattern
  const spans = [
    "col-span-12 md:col-span-7 md:row-span-2", // big
    "col-span-12 md:col-span-5",
    "col-span-6 md:col-span-5",
    "col-span-12 md:col-span-4",
    "col-span-12 md:col-span-5 md:row-span-2",
    "col-span-12 md:col-span-7",
  ];
  const span = spans[index % spans.length];

  if (service.tone === "image") {
    return (
      <article
        data-testid={`service-${service.id}`}
        className={`group relative overflow-hidden rounded-3xl ${span}`}
      >
        <img
          src={service.image}
          alt={service.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-forest-900/80 via-forest-900/20 to-transparent" />
        <div className="relative h-full p-7 md:p-9 flex flex-col justify-end text-sand-50">
          <Icon size={28} strokeWidth={1.5} className="mb-3 opacity-90" />
          <h3 className="font-serif text-3xl md:text-4xl leading-tight">
            {service.title}
          </h3>
          <p className="mt-3 text-sm md:text-base text-sand-50/85 max-w-md">
            {service.blurb}
          </p>
        </div>
      </article>
    );
  }

  const isDark = service.tone === "dark";
  return (
    <article
      data-testid={`service-${service.id}`}
      className={`group relative overflow-hidden rounded-3xl p-7 md:p-9 flex flex-col justify-between transition-colors ${span} ${
        isDark
          ? "bg-forest-900 text-sand-50 hover:bg-forest-800"
          : "bg-sand-200 text-forest-900 hover:bg-sand-100 border border-forest-900/5"
      }`}
    >
      <div className="flex items-start justify-between">
        <Icon
          size={32}
          strokeWidth={1.5}
          className={isDark ? "opacity-90" : "text-terracotta-500"}
        />
        <span
          className={`text-[11px] tracking-[0.28em] uppercase ${
            isDark ? "text-sand-50/60" : "text-forest-700/60"
          }`}
        >
          0{index + 1}
        </span>
      </div>
      <div>
        <h3 className="font-serif text-3xl md:text-4xl leading-tight">
          {service.title}
        </h3>
        <p
          className={`mt-3 text-sm md:text-base max-w-sm ${
            isDark ? "text-sand-50/80" : "text-forest-700/85"
          }`}
        >
          {service.blurb}
        </p>
      </div>
    </article>
  );
}
